// web-frontend/src/shared/utils/deviceManager.js
import logger from './logger';
/**
 * 🔥 웹용 디바이스 매니저 (UUID v4 기반)
 * - localStorage 기반 영구 저장
 * - UUID v4 생성 방식 (RFC 4122 준수)
 * - 브라우저 정보 수집 기능 포함
 */

const DEVICE_ID_KEY = 'dsk_web_device_id';

class DeviceManager {
  constructor() {
    this.deviceId = null;
    this.deviceInfo = null;
  }

  /**
   * UUID v4 생성 (RFC 4122 준수)
   * @returns {string} UUID v4 형식의 문자열
   */
  generateUUIDv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * 디바이스 ID 가져오기 (UUID v4 기반)
   * @returns {Promise<string>} 디바이스 ID
   */
  async getDeviceId() {
    try {
      // 이미 메모리에 있으면 반환
      if (this.deviceId) {
        return this.deviceId;
      }

      // localStorage에서 조회
      const storedId = localStorage.getItem(DEVICE_ID_KEY);
      if (storedId) {
        this.deviceId = storedId;
        logger.debug('✅ 저장된 디바이스 ID:', storedId);
        return storedId;
      }

      // 없으면 새로 UUID v4 생성
      const newDeviceId = this.generateUUIDv4();
      localStorage.setItem(DEVICE_ID_KEY, newDeviceId);
      this.deviceId = newDeviceId;
      logger.debug('✅ 새 디바이스 ID 생성 (UUID v4):', newDeviceId);
      return newDeviceId;
    } catch (error) {
      logger.error('❌ 디바이스 ID 가져오기 실패:', error);
      // 에러 시 임시 UUID 반환
      return this.generateUUIDv4();
    }
  }

  /**
   * 디바이스 정보 가져오기 (브라우저 정보)
   * @returns {Promise<Object>} 디바이스 정보 객체
   */
  async getDeviceInfo() {
    try {
      // 이미 메모리에 있으면 반환
      if (this.deviceInfo) {
        return this.deviceInfo;
      }

      const info = {
        platform: 'web',
        userAgent: navigator.userAgent,
        language: navigator.language,
        browserName: this.getBrowserName(),
        browserVersion: this.getBrowserVersion(),
        osName: this.getOSName(),
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        colorDepth: window.screen.colorDepth,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        isOnline: navigator.onLine,
        cookieEnabled: navigator.cookieEnabled,
      };

      this.deviceInfo = info;
      return info;
    } catch (error) {
      logger.error('❌ 디바이스 정보 가져오기 실패:', error);
      return {
        platform: 'web',
        error: error.message,
      };
    }
  }

  /**
   * 브라우저 이름 감지
   * @returns {string} 브라우저 이름
   */
  getBrowserName() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Edg')) return 'Edge';
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Opera') || userAgent.includes('OPR')) return 'Opera';
    return 'Unknown';
  }

  /**
   * 브라우저 버전 감지
   * @returns {string} 브라우저 버전
   */
  getBrowserVersion() {
    const userAgent = navigator.userAgent;
    const browserName = this.getBrowserName();

    try {
      let match;
      switch (browserName) {
        case 'Firefox':
          match = userAgent.match(/Firefox\/(\d+\.\d+)/);
          break;
        case 'Edge':
          match = userAgent.match(/Edg\/(\d+\.\d+)/);
          break;
        case 'Chrome':
          match = userAgent.match(/Chrome\/(\d+\.\d+)/);
          break;
        case 'Safari':
          match = userAgent.match(/Version\/(\d+\.\d+)/);
          break;
        case 'Opera':
          match = userAgent.match(/(?:Opera|OPR)\/(\d+\.\d+)/);
          break;
        default:
          return 'Unknown';
      }
      return match ? match[1] : 'Unknown';
    } catch (error) {
      return 'Unknown';
    }
  }

  /**
   * OS 이름 감지
   * @returns {string} OS 이름
   */
  getOSName() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Win')) return 'Windows';
    if (userAgent.includes('Mac')) return 'macOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) {
      return 'iOS';
    }
    return 'Unknown';
  }

  /**
   * 디바이스 ID 초기화 (디버그용)
   * @returns {Promise<boolean>} 성공 여부
   */
  async resetDeviceId() {
    try {
      localStorage.removeItem(DEVICE_ID_KEY);
      this.deviceId = null;
      logger.debug('✅ 디바이스 ID 초기화 완료');
      return true;
    } catch (error) {
      logger.error('❌ 디바이스 ID 초기화 실패:', error);
      return false;
    }
  }

  /**
   * 디바이스 정보 로깅 (디버그용)
   * @returns {Promise<Object|null>} 디바이스 ID와 정보
   */
  async logDeviceInfo() {
    try {
      const deviceId = await this.getDeviceId();
      const deviceInfo = await this.getDeviceInfo();
      
      logger.debug('📱 Device Information:');
      logger.debug('   - Device ID (UUID v4):', deviceId);
      logger.debug('   - Browser:', deviceInfo.browserName, deviceInfo.browserVersion);
      logger.debug('   - OS:', deviceInfo.osName);
      logger.debug('   - Screen:', deviceInfo.screenResolution);
      logger.debug('   - Language:', deviceInfo.language);
      logger.debug('   - Timezone:', deviceInfo.timezone);
      logger.debug('   - Online:', deviceInfo.isOnline);
      
      return { deviceId, deviceInfo };
    } catch (error) {
      logger.error('❌ 디바이스 정보 로깅 실패:', error);
      return null;
    }
  }
}

// 싱글톤 인스턴스
const deviceManager = new DeviceManager();

// 개발 모드에서 전역에 노출 (디버깅 용도)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.deviceManager = deviceManager;
}

export default deviceManager;